// @/services/content/store/modules/registration.js
/**
 * RegistrationHandler 
 * 
 * Handles content retrieval for the Registration flow
 * Provides locale-specific content with proper fallbacks
 */
export class RegistrationHandler {
  constructor(store) {
    this.store = store;
  }

  /**
   * Get Registration content by locale
   * @param {string} locale Locale to get content for (defaults to 'en')
   * @returns {Object|null} Content or null if not found
   */
  getContent(locale = 'en') {
    // Try requested locale first
    const localeContent = this.store.state.data.registration?.[locale];
    if (localeContent) return localeContent;
    
    // Fall back to English if different locale was requested
    if (locale !== 'en') {
      return this.store.state.data.registration?.['en'] || null;
    }
    
    return null;
  }
  
  /**
   * Get specific localized value with fallbacks
   * @param {string} key The content key to retrieve
   * @param {string} locale The locale to try first
   * @param {string} defaultValue Optional default if no translations found
   * @returns {string} The localized value or default
   */
  getText(key, locale = 'en', defaultValue = '') {
    // Try the requested locale first
    const localeContent = this.getContent(locale);
    
    if (localeContent && localeContent[key]) {
      return localeContent[key];
    }
    
    // Fall back to English if different locale was requested
    if (locale !== 'en') {
      const englishContent = this.getContent('en');
      if (englishContent && englishContent[key]) {
        return englishContent[key];
      }
    }
    
    // Return default if no translations found
    return defaultValue;
  }
  
  /**
   * Get a template string with replacements
   * @param {string} key The template key to retrieve
   * @param {Object} replacements Key-value pairs for replacements
   * @param {string} locale The locale to try first
   * @param {string} defaultValue Optional default if no translations found
   * @returns {string} The processed template string
   */
  getTemplate(key, replacements = {}, locale = 'en', defaultValue = '') {
    // Get the template string
    let template = this.getText(key, locale, defaultValue);
    
    // Replace all placeholders
    if (template && typeof template === 'string') {
      for (const [placeholder, value] of Object.entries(replacements)) {
        const regex = new RegExp(`{${placeholder}}`, 'g');
        template = template.replace(regex, value);
      }
    }
    
    return template;
  }
}